// Modules
import { useCallback, useContext, useEffect } from 'react'
import { useQuery } from '@apollo/client'
import axios from 'axios'

// Hooks
import useNotices from 'hooks/system/useNotices'
import useToken from 'hooks/authentication/useToken'

// Context
import Data from 'context/Data'
import Dispatch from 'context/Dispatch'

// Reducers
import { ACCOUNT_ACTIONS } from 'reducers/account'

// Helpers
import { checkIfAuthenticationFailed, getAuthorizationHeader, getTeam, logout, storeTeam } from 'helpers/authentication'

// Queries
import ACCOUNT from 'queries/account/Account'

// Constants
import { TERMS_OF_SERVICE_UPDATE_DATE } from 'constants/content'
import { ACCOUNT_DATA_URL } from 'constants/environment'

// Hook: Account > Account
function useAccount (isRoot = false) {
	// Data
	const token = useToken()
	const { addNotice } = useNotices()
	const { account } = useContext(Data)
	const dispatch = useContext(Dispatch)
	const { data: accountData, error: accountError } = useQuery(ACCOUNT, {
		fetchPolicy: 'no-cache',
		skip: !isRoot || !!account || !token
	})

	// Functions (Memoized)
	const updateAccount = useCallback(data => {
		dispatch({
			type: ACCOUNT_ACTIONS.SET,
			payload: {
				...(data || {})
			}
		})
	}, [dispatch])

	const handleAccountError = useCallback(error => {
		if (checkIfAuthenticationFailed(error)) logout()
		else {
			addNotice({ code: 'client.accountFetchingError', log: error, type: 'error' })
			updateAccount({ hasLoadingError: true })
		}
	}, [addNotice, updateAccount])

	// Effects
	useEffect(() => {
		async function setTermsAcceptingData() {
			try {
				const { data } = await axios({
					headers: { ...getAuthorizationHeader() },
					method: 'GET',
					url: ACCOUNT_DATA_URL
				})
				if (!!data) {
					updateAccount({
						isLatestTermsOfServiceAccepted: (
							!!data.termsAcceptedAt &&
							!(new Date(data.termsAcceptedAt).getTime() < new Date(TERMS_OF_SERVICE_UPDATE_DATE).getTime()) // WARNING: Parse as UTC, do not strip the "Z"
						)
					})
				} else handleAccountError()
			} catch (error) {
				handleAccountError(error)
			}
		}
		// WARNING: "hasLoadingError" also bounds the retries, the effect re-runs on every account update
		if (!!account && !account.hasLoadingError && typeof account.isLatestTermsOfServiceAccepted !== 'boolean') setTermsAcceptingData()
	}, [account, handleAccountError, updateAccount])
	useEffect(() => {
		if (!!accountData?.user) {
			const defaultTeamId = accountData.user.team?.id
			const storedTeamId = getTeam()
			const teamId = !!storedTeamId && accountData.user.teams?.some(team => team.id.toString() === storedTeamId.toString()) ? storedTeamId : defaultTeamId // Used toString on both side to avoid possible problems caused by a change in BE implementation
			storeTeam(teamId)
			updateAccount({
				...(accountData.user || {}),
				belespritOnboardingTime: accountData.user.belEspritOnboardedAt,
				isPhoneNumberVerified: !!accountData.user.phoneNumberVerifiedAt,
				isPhoneNumberVerifiedBefore: !!accountData.user.phoneNumberVerifiedBefore,
				team: { id: teamId } // Current team
			})
		}
	}, [accountData?.user, updateAccount])
	useEffect(() => {
		if (!isRoot || !!account || !accountError) return
		handleAccountError(accountError)
	}, [account, accountError, handleAccountError, isRoot])
}

// Export
export default useAccount
